import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { List, Row, Section } from './LegalPage';
import { LEGAL } from '../../config/legal';

/**
 * Written from the actual data flows in this repo, not from a template:
 * the DynamoDB item shapes in backend/src/auth/authHandler.js, the Bedrock
 * calls in generateChallenge.js / evaluateChallenge.js, the SES sends in
 * emailService.js, and the localStorage session in AuthService.ts.
 * If those change, this changes.
 */
const Privacy: React.FC = () => (
  <LegalPage
    title="Privacy Policy"
    summary={`What ${LEGAL.SITE_NAME} collects, why, where it goes, and what you can ask us to do about it.`}
  >
    <Section heading="The short version">
      <p style={{ margin: 0 }}>
        We collect your name, your email address and what you do while learning. We use it to run
        your account and show your progress. We do not sell it, we do not use advertising trackers,
        and there is no analytics service watching you. The prompts you write are sent to Amazon Web
        Services to be scored, because that is how the exercises work.
      </p>
    </Section>

    <Section heading="Who is responsible for your data">
      <p style={{ margin: 0 }}>
        Your data is controlled by {LEGAL.ENTITY_NAME}. For anything in this policy, including the
        requests described under <Link to="/data-rights" style={{ color: 'var(--ember-strong)', fontWeight: 700, textDecoration: 'none' }}>your data rights</Link>,
        contact <strong>{LEGAL.CONTACT_EMAIL}</strong>.
      </p>
    </Section>

    <Section heading="Early-access inquiries">
      <p>When you request early access, we collect your first and last name, company, business email, optional phone number, and optional message. We use these details to respond to your inquiry and discuss early-access availability, not to create an account. Your inquiry is sent to the product owner's inbox through our email-delivery provider. Cloudflare processes the request and security-check data to help prevent abuse. Do not include confidential information. You can request deletion or ask us to stop contacting you using the privacy contact above.</p>
    </Section>

    <Section heading="What we collect, and why">
      <div>
        <Row term="Name">
          Given and family name, from the signup form. Used to address you in the app and on the
          leaderboard.
        </Row>
        <Row term="Email address">
          Also your username. Used to sign you in, to send password-reset codes, and to contact you
          about your account.
        </Row>
        <Row term="Password">
          Never stored. We store a bcrypt hash, which cannot be reversed into your password. Nobody
          operating this service can read it.
        </Row>
        <Row term="Learning activity">
          Challenges attempted and completed, scores, points, level, streak, badges and quest
          progress. This is the product: without it there is nothing to show you.
        </Row>
        <Row term="Prompts you write">
          The text you submit in challenges and the Playground, and the model's response, so your
          work can be scored and shown back to you.
        </Row>
        <Row term="Technical logs">
          Server logs recording that a request happened, and errors when they occur. Used to keep
          the service working and to investigate abuse.
        </Row>
      </div>
      <p style={{ margin: '14px 0 0' }}>
        Apart from an optional phone number for early-access inquiries, we do not ask for your date of birth, address, or payment
        details — there is no paid plan today, so no card is ever collected.
      </p>
    </Section>

    <Section heading="Your prompts and the AI model">
      <p style={{ margin: 0 }}>
        Challenges are generated and graded by a large language model running on Amazon Bedrock in{' '}
        {LEGAL.HOSTING_REGION}. When you submit a prompt, that text and the model's response are sent
        to Bedrock for processing, and stored against your account so your progress and feedback
        persist. Treat prompts the way you would treat anything typed into a shared work tool:{' '}
        <strong>do not put confidential, personal or sensitive information into a challenge or the
        Playground.</strong> AWS's handling of this content is governed by their service terms; we do
        not use your prompts to train any model of our own.
      </p>
    </Section>

    <Section heading="Cookies and what's stored in your browser">
      <p style={{ margin: 0 }}>
        {LEGAL.SITE_NAME} does not use cookies for tracking, advertising or analytics. It stores two
        items in your browser's <span className="ws-mono">localStorage</span>: your session token and
        a copy of your basic profile, so you stay signed in between visits. Both are strictly
        necessary to keep you logged in, both are removed when you sign out, and neither is shared
        with anyone. That is why you are not asked to accept a cookie banner — there is nothing to
        consent to.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        One exception worth naming: the site loads its fonts from Google Fonts, which means your
        browser makes a request to Google and Google can see your IP address. We are moving to
        self-hosted fonts to remove that.
      </p>
    </Section>

    <Section heading="Who else touches your data">
      <p style={{ margin: '0 0 4px' }}>
        We use these providers to run the service. We do not sell or rent your data to anyone, and
        there are no advertising or data-broker relationships.
      </p>
      <div>
        <Row term="Amazon Web Services">
          Hosting, database, email delivery and the AI model, all in {LEGAL.HOSTING_REGION}.
        </Row>
        <Row term="Google Fonts">
          Serves the site's typefaces; receives your IP address as a result. Being removed — see
          above.
        </Row>
      </div>
    </Section>

    <Section heading="Where your data is held">
      <p style={{ margin: 0 }}>
        In the United States, in {LEGAL.HOSTING_REGION}. If you are in the UK, the EEA or
        Switzerland, this means your data is transferred outside your home region. That transfer
        relies on the Standard Contractual Clauses in AWS's data processing addendum. If you are not
        comfortable with US processing, please do not create an account.
      </p>
    </Section>

    <Section heading="How long we keep it">
      <p style={{ margin: 0 }}>
        Your account and learning history are kept for as long as your account exists, because
        deleting them would delete your progress. Password-reset codes expire after one hour, and
        email-verification codes after 24 hours. Server logs are kept on a rolling short-term basis
        for troubleshooting. When you ask us to delete your account, see{' '}
        <Link to="/data-rights" style={{ color: 'var(--ember-strong)', fontWeight: 700, textDecoration: 'none' }}>your data rights</Link>.
      </p>
    </Section>

    <Section heading="How it's protected">
      <List
        items={[
          'Passwords are hashed with bcrypt and never stored in readable form.',
          'Traffic is encrypted in transit over HTTPS.',
          'Sessions use signed tokens that expire, and every API request is checked against one.',
          'Access to the production database is limited to the operator of the service.',
        ]}
      />
      <p style={{ margin: '10px 0 0' }}>
        No service can promise perfect security, and we will not pretend otherwise. If we discover a
        breach affecting your personal data, we will tell you and the relevant regulator as the law
        requires.
      </p>
    </Section>

    <Section heading="Children">
      <p style={{ margin: 0 }}>
        {LEGAL.SITE_NAME} is not intended for children under 16. We do not knowingly collect data
        from them. If you believe a child has created an account, email {LEGAL.CONTACT_EMAIL} and we
        will delete it.
      </p>
    </Section>

    <Section heading="Changes to this policy">
      <p style={{ margin: 0 }}>
        If we change how we handle your data, we will update this page and the "last updated" date
        above. If the change is significant, we will email you rather than rely on you noticing.
      </p>
    </Section>
  </LegalPage>
);

export default Privacy;
